import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  DeleteOutline,
  Edit,
  PhotoCamera,
  Search,
  Pets,
  Medication as PrescriptionIcon,
} from '@mui/icons-material';
import clientDashboardService, {
  ClientPatient,
  ClientPatientAlert,
  ClientPatientReminder,
  ClientOwnerMedicalRecord,
} from '@services/clientDashboardService';
import { API_ORIGIN } from '@config/api';

function resolveMediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

const ClientAnimalsModule: React.FC = () => {
  const navigate = useNavigate();
  const { id: routePetId } = useParams<{ id?: string }>();
  const [pets, setPets] = useState<ClientPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [speciesFilter, setSpeciesFilter] = useState('all');

  const [vaccinations, setVaccinations] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [medical, setMedical] = useState<ClientOwnerMedicalRecord | null>(null);
  const [reminders, setReminders] = useState<ClientPatientReminder[]>([]);
  const [alerts, setAlerts] = useState<ClientPatientAlert[]>([]);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClientPatient | null>(null);
  const [form, setForm] = useState({
    name: '',
    species: 'dog',
    breed: '',
    sex: 'male',
    color: '',
    weight: '' as string,
    dateOfBirth: '',
    microchip: '',
  });

  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const newRouteHandledRef = useRef(false);

  const selected = useMemo(() => pets.find((p) => p.id === selectedId) || null, [pets, selectedId]);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setListError('');
      const data = await clientDashboardService.getPatients({
        q: search || undefined,
        lifecycle: lifecycleFilter === 'all' ? undefined : lifecycleFilter,
        species: speciesFilter === 'all' ? undefined : speciesFilter,
      });
      setPets(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setListError(e?.response?.data?.error || 'Nu am putut încărca animalele.');
      setPets([]);
    } finally {
      setLoading(false);
    }
  }, [search, lifecycleFilter, speciesFilter]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      loadList();
    }, 300);
    return () => window.clearTimeout(t);
  }, [loadList]);

  useEffect(() => {
    if (routePetId === 'new') {
      if (!newRouteHandledRef.current) {
        newRouteHandledRef.current = true;
        setSelectedId(null);
        setEditing(null);
        setForm({
          name: '',
          species: 'dog',
          breed: '',
          sex: 'male',
          color: '',
          weight: '',
          dateOfBirth: '',
          microchip: '',
        });
        setDialogOpen(true);
      }
      return;
    }
    newRouteHandledRef.current = false;
    if (routePetId && pets.some((p) => p.id === routePetId)) {
      setSelectedId(routePetId);
    }
  }, [routePetId, pets]);

  const closePetDialog = () => {
    setDialogOpen(false);
    if (routePetId === 'new') {
      navigate('/animals', { replace: true });
    }
  };

  const loadDetailExtras = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const [v, d, a, m, r, al, rx] = await Promise.all([
        clientDashboardService.getPatientVaccinations(id),
        clientDashboardService.getPatientDocuments(id),
        clientDashboardService.getPatientAppointments(id),
        clientDashboardService.getPatientMedicalRecord(id),
        clientDashboardService.getPatientReminders(id),
        clientDashboardService.getPatientAlerts(id),
        clientDashboardService.getPatientPrescriptions(id).catch(() => []),
      ]);
      setVaccinations(v);
      setDocuments(d);
      setAppointments(a);
      setMedical(m);
      setReminders(r);
      setAlerts(al);
      setPrescriptions(Array.isArray(rx) ? rx : []);
    } catch (e: any) {
      setDetailError(e?.response?.data?.error || 'Eroare la încărcarea detaliilor.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadDetailExtras(selectedId);
  }, [selectedId, loadDetailExtras]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      species: 'dog',
      breed: '',
      sex: 'male',
      color: '',
      weight: '',
      dateOfBirth: '',
      microchip: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (p: ClientPatient) => {
    setEditing(p);
    setForm({
      name: p.name,
      species: p.species,
      breed: p.breed || '',
      sex: p.sex || 'male',
      color: p.color || '',
      weight: p.weight != null ? String(p.weight) : '',
      dateOfBirth: p.dateOfBirth ? String(p.dateOfBirth).slice(0, 10) : '',
      microchip: p.microchip || '',
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) {
      setSnack({ open: true, message: 'Numele este obligatoriu.', severity: 'error' });
      return;
    }
    try {
      if (editing) {
        const updated = await clientDashboardService.updatePatient(editing.id, {
          name: form.name.trim(),
          species: form.species,
          breed: form.breed.trim() || 'Necunoscută',
          sex: form.sex,
          gender: form.sex,
          color: form.color.trim() || 'Necunoscută',
          weight: form.weight === '' ? null : Number(form.weight),
          dateOfBirth: form.dateOfBirth || null,
          birthDate: form.dateOfBirth || null,
          microchip: form.microchip.trim() || null,
          microchipId: form.microchip.trim() || null,
        });
        setPets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        setSnack({ open: true, message: 'Animal actualizat.', severity: 'success' });
        setDialogOpen(false);
      } else {
        const created = await clientDashboardService.createPatient({
          name: form.name.trim(),
          species: form.species,
          sex: form.sex,
          breed: form.breed.trim() || 'Necunoscută',
          color: form.color.trim() || 'Necunoscută',
          weight: form.weight === '' ? null : Number(form.weight),
          dateOfBirth: form.dateOfBirth || null,
          microchip: form.microchip.trim() || null,
        });
        setPets((prev) => [created, ...prev]);
        setSelectedId(created.id);
        setSnack({ open: true, message: 'Animal adăugat.', severity: 'success' });
        setDialogOpen(false);
        if (routePetId === 'new') {
          navigate(`/animals/${created.id}`, { replace: true });
        }
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Salvare eșuată.';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadingPhoto(true);
    try {
      const { photo_url } = await clientDashboardService.uploadPatientPhoto(selected.id, file);
      if (!photo_url) {
        setSnack({ open: true, message: 'Răspuns incomplet de la server.', severity: 'error' });
        return;
      }
      const refreshed = await clientDashboardService.getPatient(selected.id);
      setPets((prev) => prev.map((x) => (x.id === refreshed.id ? refreshed : x)));
      setSnack({ open: true, message: 'Poză încărcată.', severity: 'success' });
    } catch (err: any) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || 'Upload eșuat.',
        severity: 'error',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const archiveOrDelete = async () => {
    if (!selected) return;
    if (!window.confirm('Ștergi sau arhivezi acest animal? Dacă există istoric medical, va fi arhivat.')) return;
    try {
      const res = await clientDashboardService.deletePatient(selected.id);
      if (res.archived) {
        setSnack({ open: true, message: res.message || 'Animal arhivat.', severity: 'success' });
      } else {
        setSnack({ open: true, message: 'Animal șters.', severity: 'success' });
        setSelectedId(null);
      }
      await loadList();
    } catch (e: any) {
      setSnack({ open: true, message: e?.response?.data?.error || 'Operație eșuată.', severity: 'error' });
    }
  };

  const patchLifecycle = async (lifecycleStatus: string, notes?: string) => {
    if (!selected) return;
    try {
      const updated = await clientDashboardService.updatePatient(selected.id, {
        lifecycleStatus,
        lifecycleNotes: notes ?? selected.lifecycleNotes,
      });
      setPets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setSnack({ open: true, message: 'Status actualizat.', severity: 'success' });
    } catch (e: any) {
      setSnack({ open: true, message: e?.response?.data?.error || 'Nu am putut actualiza statusul.', severity: 'error' });
    }
  };

  const patchNeuter = async (isNeutered: boolean, neuteredDate: string) => {
    if (!selected) return;
    try {
      const updated = await clientDashboardService.updatePatient(selected.id, {
        isNeutered,
        neuteredDate: isNeutered && neuteredDate ? neuteredDate : null,
      });
      setPets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setSnack({ open: true, message: 'Sterilizare salvată.', severity: 'success' });
    } catch (e: any) {
      setSnack({ open: true, message: e?.response?.data?.error || 'Eroare la salvare.', severity: 'error' });
    }
  };

  const filteredGrid = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pets.filter((p) => {
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.breed || '').toLowerCase().includes(q) ||
        (p.microchip || '').toLowerCase().includes(q)
      );
    });
  }, [pets, search]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate('/dashboard')} size="small" aria-label="Înapoi">
          <ArrowBack />
        </IconButton>
        <Pets color="primary" />
        <Typography variant="h5" component="h1" fontWeight={600}>
          Animalele mele
        </Typography>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <TextField
          size="small"
          placeholder="Caută după nume, rasă, microchip..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={lifecycleFilter}
            onChange={(e) => setLifecycleFilter(e.target.value)}
          >
            <MenuItem value="all">Toate</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="deceased">Decedat</MenuItem>
            <MenuItem value="lost">Pierdut</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Specie</InputLabel>
          <Select label="Specie" value={speciesFilter} onChange={(e) => setSpeciesFilter(e.target.value)}>
            <MenuItem value="all">Toate</MenuItem>
            <MenuItem value="dog">Câine</MenuItem>
            <MenuItem value="cat">Pisică</MenuItem>
            <MenuItem value="bird">Pasăre</MenuItem>
            <MenuItem value="rabbit">Iepure</MenuItem>
            <MenuItem value="horse">Cal</MenuItem>
            <MenuItem value="other">Altele</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Adaugă animal
        </Button>
      </Box>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : !selectedId ? (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
          gap={2}
        >
          {filteredGrid.length === 0 ? (
            <Typography color="text.secondary">Niciun animal. Adaugă primul animal al tău.</Typography>
          ) : (
            filteredGrid.map((p) => (
              <Card key={p.id} sx={{ cursor: 'pointer' }} onClick={() => setSelectedId(p.id)}>
                <CardContent>
                  <Box display="flex" gap={2} alignItems="center">
                    <Avatar src={resolveMediaUrl(p.photo_url)} sx={{ width: 56, height: 56 }}>
                      {p.name.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Typography fontWeight={600}>{p.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {p.breed} · {p.species}
                      </Typography>
                      <Chip
                        size="small"
                        label={p.lifecycleStatus || 'active'}
                        sx={{ mt: 0.5 }}
                        color={p.lifecycleStatus === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        selected && (
          <Card>
            <CardContent>
              <Button startIcon={<ArrowBack />} onClick={() => setSelectedId(null)} sx={{ mb: 2 }}>
                Înapoi la listă
              </Button>

              <Box display="flex" flexWrap="wrap" gap={3} alignItems="flex-start">
                <Box position="relative">
                  <Avatar src={resolveMediaUrl(selected.photo_url)} sx={{ width: 96, height: 96 }}>
                    {selected.name.charAt(0)}
                  </Avatar>
                  <Button
                    component="label"
                    size="small"
                    startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <PhotoCamera />}
                    disabled={uploadingPhoto}
                    sx={{ mt: 1 }}
                  >
                    Poză
                    <input type="file" hidden accept="image/*" onChange={onPhoto} />
                  </Button>
                </Box>
                <Box flex={1} minWidth={260}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" fontWeight={600}>
                      {selected.name}
                    </Typography>
                    <IconButton size="small" onClick={() => openEdit(selected)} aria-label="Editează">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={archiveOrDelete} aria-label="Șterge">
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography color="text.secondary">
                    {selected.breed} · {selected.sex === 'male' ? 'Mascul' : 'Femelă'} ·{' '}
                    {selected.microchip ? `Microchip ${selected.microchip}` : 'Fără microchip'}
                  </Typography>
                  {selected.dateOfBirth && (
                    <Typography variant="body2">Născut: {String(selected.dateOfBirth).slice(0, 10)}</Typography>
                  )}
                  {selected.weight != null && <Typography variant="body2">Greutate: {selected.weight} kg</Typography>}
                  <Box mt={1} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={selected.lifecycleStatus || 'active'}
                        onChange={(e) => patchLifecycle(e.target.value)}
                      >
                        <MenuItem value="active">Activ</MenuItem>
                        <MenuItem value="inactive">Inactiv</MenuItem>
                        <MenuItem value="deceased">Decedat</MenuItem>
                        <MenuItem value="lost">Pierdut</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {(selected.lifecycleStatus === 'deceased' || selected.lifecycleStatus === 'lost') && (
                    <TextField
                      fullWidth
                      size="small"
                      margin="normal"
                      label="Note (opțional)"
                      value={selected.lifecycleNotes || ''}
                      onChange={(e) =>
                        setPets((prev) =>
                          prev.map((x) => (x.id === selected.id ? { ...x, lifecycleNotes: e.target.value } : x))
                        )
                      }
                      onBlur={(e) => patchLifecycle(selected.lifecycleStatus || 'active', e.target.value)}
                    />
                  )}
                </Box>
              </Box>

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
                <Tab label="Rezumat" />
                <Tab label="Vaccinări" />
                <Tab label="Memento-uri" />
                <Tab label="Documente" />
                <Tab label="Programări" />
                <Tab label="Istoric medical" />
                <Tab label="Prescripții" />
                <Tab label="Alerte" />
              </Tabs>

              {detailError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {detailError}
                </Alert>
              )}
              {detailLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {tab === 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Alergii (doar informare, modificate de clinică)
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                        {(selected.allergies || []).length ? (
                          selected.allergies.map((a) => <Chip key={a} size="small" label={a} />)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nicio alergie înregistrată.
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Afecțiuni cronice
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                        {(selected.chronicConditions || []).length ? (
                          selected.chronicConditions.map((a) => <Chip key={a} size="small" label={a} />)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Niciuna.
                          </Typography>
                        )}
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <NeuterSection pet={selected} onSave={patchNeuter} />
                    </Box>
                  )}
                  {tab === 1 && (
                    <List dense>
                      {vaccinations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nu există vaccinări înregistrate.
                        </Typography>
                      ) : (
                        vaccinations.map((v) => (
                          <ListItem key={v.id} divider>
                            <ListItemText
                              primary={v.vaccine_name}
                              secondary={`${v.administered_date || ''} · status: ${v.status || ''}`}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                  {tab === 2 && (
                    <List dense>
                      {reminders.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Niciun memento de prevenție.
                        </Typography>
                      ) : (
                        reminders.map((r) => (
                          <ListItem key={r.id} divider>
                            <ListItemText
                              primary={`${r.name} (${r.kind})`}
                              secondary={`Scadență: ${r.dueAt} · ${r.status}`}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                  {tab === 3 && (
                    <List dense>
                      {documents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nu există documente partajate.
                        </Typography>
                      ) : (
                        documents.map((d: any) => (
                          <ListItem key={`${d.source}-${d.id}`} divider>
                            <ListItemText
                              primary={d.name}
                              secondary={d.category || d.mime_type}
                            />
                            {d.file_url && resolveMediaUrl(d.file_url) && (
                              <Button
                                component="a"
                                size="small"
                                href={resolveMediaUrl(d.file_url)!}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Deschide
                              </Button>
                            )}
                            {d.source === 'documents' && (
                              <Button
                                size="small"
                                onClick={async () => {
                                  try {
                                    const { download_url } = await clientDashboardService.getDownloadUrl(d.id);
                                    window.open(download_url, '_blank');
                                  } catch {
                                    setSnack({ open: true, message: 'Descărcare indisponibilă.', severity: 'error' });
                                  }
                                }}
                              >
                                Descarcă
                              </Button>
                            )}
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                  {tab === 4 && (
                    <List dense>
                      {appointments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nicio programare pentru acest animal.
                        </Typography>
                      ) : (
                        appointments.map((a: any) => (
                          <ListItem key={a.id} divider>
                            <ListItemText
                              primary={`${a.clinic?.name || 'Clinică'} — ${a.reason || ''}`}
                              secondary={`${a.scheduledAt} · ${a.status}`}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                  {tab === 5 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Consultații finalizate (semnate/blocate)
                      </Typography>
                      <List dense>
                        {!medical?.visits?.length ? (
                          <Typography variant="body2" color="text.secondary">
                            Nu există vizite înregistrate sau nu sunt vizibile.
                          </Typography>
                        ) : (
                          medical.visits.map((m) => (
                            <ListItem key={m.id} alignItems="flex-start" divider>
                              <ListItemText
                                primary={`${m.visit_date} — ${m.visit_type}`}
                                secondary={
                                  <>
                                    {m.clinic_name && (
                                      <Typography variant="caption" display="block">
                                        {m.clinic_name}
                                        {m.veterinarian_name ? ` · ${m.veterinarian_name}` : ''}
                                      </Typography>
                                    )}
                                    {m.diagnosis && (
                                      <Typography variant="body2" display="block">
                                        Diagnostic: {m.diagnosis}
                                      </Typography>
                                    )}
                                    {m.treatment_plan && (
                                      <Typography variant="body2" display="block">
                                        Tratament: {m.treatment_plan}
                                      </Typography>
                                    )}
                                    {m.recommendations && (
                                      <Typography variant="body2" display="block">
                                        Recomandări: {m.recommendations}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                          ))
                        )}
                      </List>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Cronologie (fără note interne)
                      </Typography>
                      <List dense>
                        {!medical?.timeline?.length ? (
                          <Typography variant="body2" color="text.secondary">
                            Nu există evenimente în cronologie.
                          </Typography>
                        ) : (
                          medical.timeline.slice(0, 80).map((e) => (
                            <ListItem key={e.id} alignItems="flex-start" divider>
                              <ListItemText
                                primary={e.title}
                                secondary={
                                  <>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      {new Date(e.at).toLocaleString()} · {e.kind}
                                    </Typography>
                                    {e.summary && (
                                      <Typography variant="body2" display="block">
                                        {e.summary}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>
                  )}
                  {tab === 6 && (
                    <List dense>
                      {prescriptions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nu există prescripții emise vizibile pentru acest animal.
                        </Typography>
                      ) : (
                        prescriptions.map((rx: any) => (
                          <ListItem key={rx.id} divider secondaryAction={
                            <Button
                              size="small"
                              startIcon={<PrescriptionIcon />}
                              onClick={async () => {
                                if (!selectedId) return;
                                try {
                                  await clientDashboardService.downloadPatientPrescriptionPdf(selectedId, rx.id);
                                } catch {
                                  setSnack({ open: true, message: 'Nu am putut descărca PDF-ul.', severity: 'error' });
                                }
                              }}
                            >
                              PDF
                            </Button>
                          }>
                            <ListItemText
                              primary={`Status: ${rx.display_status || rx.status || '—'}`}
                              secondary={
                                <>
                                  {rx.patient_instructions && (
                                    <Typography variant="body2" display="block">
                                      Instrucțiuni: {rx.patient_instructions}
                                    </Typography>
                                  )}
                                  {(rx.items || []).length > 0 && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      {(rx.items || []).map((it: any) => it.medication_name).filter(Boolean).join(', ')}
                                    </Typography>
                                  )}
                                  {(rx.addendums || []).length > 0 && (
                                    <Typography variant="body2" display="block" sx={{ mt: 1 }}>
                                      <strong>Addendum:</strong>{' '}
                                      {(rx.addendums || []).map((a: { text?: string }) => a.text).filter(Boolean).join(' · ')}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                  {tab === 7 && (
                    <List dense>
                      {alerts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nicio alertă recentă pentru acest animal.
                        </Typography>
                      ) : (
                        alerts.map((a) => (
                          <ListItem key={a.id} divider>
                            <ListItemText primary={a.title} secondary={a.body} />
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )
      )}

      <Dialog open={dialogOpen} onClose={closePetDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editează animal' : 'Animal nou'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Nume" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          <FormControl fullWidth>
            <InputLabel>Specie</InputLabel>
            <Select
              label="Specie"
              value={form.species}
              onChange={(e) => setForm((f) => ({ ...f, species: e.target.value }))}
            >
              <MenuItem value="dog">Câine</MenuItem>
              <MenuItem value="cat">Pisică</MenuItem>
              <MenuItem value="bird">Pasăre</MenuItem>
              <MenuItem value="rabbit">Iepure</MenuItem>
              <MenuItem value="horse">Cal</MenuItem>
              <MenuItem value="other">Altele</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Rasă" value={form.breed} onChange={(e) => setForm((f) => ({ ...f, breed: e.target.value }))} />
          <FormControl fullWidth>
            <InputLabel>Sex</InputLabel>
            <Select
              label="Sex"
              value={form.sex}
              onChange={(e) => setForm((f) => ({ ...f, sex: e.target.value }))}
            >
              <MenuItem value="male">Mascul</MenuItem>
              <MenuItem value="female">Femelă</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Culoare" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} />
          <TextField
            label="Greutate (kg, opțional)"
            value={form.weight}
            onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
          />
          <TextField
            label="Data nașterii"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.dateOfBirth}
            onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
          />
          <TextField
            label="Microchip (opțional)"
            value={form.microchip}
            onChange={(e) => setForm((f) => ({ ...f, microchip: e.target.value }))}
            helperText="9–32 caractere alfanumerice, dacă îl completezi."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePetDialog}>Anulează</Button>
          <Button variant="contained" onClick={saveForm}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

function NeuterSection({
  pet,
  onSave,
}: {
  pet: ClientPatient;
  onSave: (isNeutered: boolean, neuteredDate: string) => void;
}) {
  const [isN, setIsN] = useState(Boolean(pet.isNeutered));
  const [dt, setDt] = useState(pet.neuteredDate ? String(pet.neuteredDate).slice(0, 10) : '');
  useEffect(() => {
    setIsN(Boolean(pet.isNeutered));
    setDt(pet.neuteredDate ? String(pet.neuteredDate).slice(0, 10) : '');
  }, [pet.id, pet.isNeutered, pet.neuteredDate]);
  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={isN}
            onChange={(e) => {
              setIsN(e.target.checked);
              onSave(e.target.checked, dt);
            }}
          />
        }
        label="Sterilizat / castrat"
      />
      {isN && (
        <TextField
          type="date"
          size="small"
          label="Data sterilizării"
          InputLabelProps={{ shrink: true }}
          value={dt}
          onChange={(e) => setDt(e.target.value)}
          onBlur={() => onSave(isN, dt)}
          sx={{ ml: 1, maxWidth: 200 }}
        />
      )}
    </Box>
  );
}

export default ClientAnimalsModule;
